import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { DeviceType, DevicesPayload } from '../../devices/components/devices.models';
import {
  FieldOperationFilterName,
  type FieldOperationsList,
  type FieldOperationsListItem,
  type MeterModel,
  type MeterSerialNumber,
  type OperationDate,
  type Port,
  type ServiceLocationIds
} from './field-operations.types';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { TranslateService } from '@ngx-translate/core';
import { FieldOperationsApiService } from '../api/field-operations-api.service';
import { FieldOperationsListResponse, FieldOperationsListItemResponse } from '../api/field-operations-api.types';

const INSTALLATION_DEFAULT_FILTER = {
  filterType: FieldOperationFilterName.DeviceType,
  filteredValues: [DeviceType.MTU]
};

@Injectable({ providedIn: 'root' })
export class FieldOperationsService {
  private readonly fieldOperationsApiService = inject(FieldOperationsApiService);
  private readonly timeZoneService = inject(TimezoneService);
  private readonly translate = inject(TranslateService);

  readList(tableState: DevicesPayload): Observable<FieldOperationsList> {
    const initState: FieldOperationsList = { data: [], total: 0 };

    const payload = {
      ...tableState,
      filters: [...(tableState.filters ?? []), INSTALLATION_DEFAULT_FILTER]
    };

    return this.fieldOperationsApiService.readList(payload).pipe(
      map(({ items, totalCount: total }: FieldOperationsListResponse): FieldOperationsList => {
        const data = items.map(
          ({
            commDeviceId,
            deviceUID,
            installationDateTimeUtc,
            installedBy,
            installationReason,
            childItems
          }: FieldOperationsListItemResponse): FieldOperationsListItem => {
            const initialReadDateTime: OperationDate[] = [];
            const wakeupDateTime: OperationDate[] = [];
            const childPort: Port[] = [];
            const childServiceLocationIds: ServiceLocationIds[] = [];
            const meterModel: MeterModel[] = [];
            const meterSerialNumber: MeterSerialNumber[] = [];

            for (const {
              initialReadDateTimeUtc,
              wakeupDateTimeUtc,
              port,
              serviceLocationIds,
              model,
              serialNumber,
              deviceUID
            } of childItems) {
              initialReadDateTime.push(
                initialReadDateTimeUtc
                  ? { date: this.timeZoneService.formatLong(initialReadDateTimeUtc) }
                  : { date: initialReadDateTimeUtc }
              );

              wakeupDateTime.push(
                wakeupDateTimeUtc
                  ? { date: this.timeZoneService.formatLong(wakeupDateTimeUtc) }
                  : { date: wakeupDateTimeUtc }
              );

              childPort.push({ port });
              childServiceLocationIds.push({ serviceLocationIds });
              meterModel.push({ model });
              meterSerialNumber.push({ serialNumber, deviceUID });
            }
            return {
              initialReadDateTime,
              installationDateTime: this.timeZoneService.formatLong(installationDateTimeUtc),
              installationReason: this.#getInstallationReasonTranslation(installationReason),
              installedBy,
              deviceUID,
              mtuDeviceLabel: commDeviceId,
              wakeupDateTime,
              serviceLocationIds: childServiceLocationIds,
              port: childPort,
              meterModel,
              meterSerialNumber,
              childItems
            };
          }
        );
        return { data, total };
      }),
      catchError((): Observable<FieldOperationsList> => of(initState))
    );
  }

  #getInstallationReasonTranslation(installationReason: string): string {
    switch (installationReason) {
      case 'AddMeter':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.ADD_METER');
      case 'AddMTU':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.ADD_MTU');
      case 'AddMTUAddMeter':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.ADD_MTU_ADD_METER');
      case 'ReplaceMeter':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.REPLACE_METER');
      case 'ReplaceMTU':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.REPLACE_MTU');
      case 'ReplaceMTUReplaceMeter':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.REPLACE_MTU_REPLACE_METER');
      case 'ReadMTU':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.READ_MTU');
      case 'TurnOffMTU':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.TURN_OFF_MTU');
      case 'ValvePosition':
        return this.translate.instant('FILTER.INSTALLATION_REASONS.VALVE_POSITION');
      case 'Unknown':
        return this.translate.instant('FILTER.COMMON.UNKNOWN');
      default:
        return '';
    }
  }
}
